import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { sanctions, type SanctionsCheck } from "@/api/sanctions";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Sanctions / PEP / OFAC review queue.
 *
 * Compliance officer's home base: lists checks filtered by status,
 * shows the vendor + hit categories, and lets the officer decide a
 * REVIEW row to PASS or FAIL with a note. Manual rescan triggers
 * a fresh vendor call for one user.
 */
export function AdminSanctionsPage() {
  const [status, setStatus] = useState<SanctionsCheck["status"] | "">("REVIEW");
  const [decideTarget, setDecideTarget] = useState<SanctionsCheck | null>(null);

  const list = useQuery({
    queryKey: ["admin-sanctions", status],
    queryFn: () => sanctions.list(status || undefined),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Sanctions queue</h1>
          <p className="text-2xs text-term-mute">
            Sanctions / PEP / adverse-media checks. REVIEW rows need
            adjudication before the user can trade.
          </p>
        </div>
        <Select
          className="w-48"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="REVIEW">REVIEW (pending)</option>
          <option value="FAIL">FAIL</option>
          <option value="PASS">PASS</option>
          <option value="ERROR">ERROR</option>
          <option value="">All</option>
        </Select>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No checks"
              description={
                status === "REVIEW"
                  ? "Nothing to adjudicate. New REVIEW rows appear here automatically when the vendor returns matches that aren't sanctions."
                  : "No checks match the current filter."
              }
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Vendor</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Hits</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Categories</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">When</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => (
                  <tr key={c.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5">
                      <div className="text-term-fg">{c.user_email ?? "(no email)"}</div>
                      {c.user_name && (
                        <div className="text-2xs text-term-dim">{c.user_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-term-dim">{c.vendor}</td>
                    <td className="px-3 py-1.5">{statusBadge(c.status)}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {c.hit_count}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {c.hit_categories.map((cat) => (
                          <Badge key={cat} tone="warn">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {new Date(c.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        {(c.status === "REVIEW" || c.status === "ERROR") && (
                          <Button onClick={() => setDecideTarget(c)}>Decide</Button>
                        )}
                        <RescanButton userId={c.user_id} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {decideTarget && (
        <DecideModal
          check={decideTarget}
          onClose={() => setDecideTarget(null)}
        />
      )}
    </div>
  );
}

function statusBadge(s: SanctionsCheck["status"]) {
  const tone =
    s === "PASS"
      ? "good"
      : s === "FAIL"
      ? "bad"
      : s === "REVIEW"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function DecideModal({
  check,
  onClose,
}: {
  check: SanctionsCheck;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [decision, setDecision] = useState<"PASS" | "FAIL">("PASS");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: () =>
      sanctions.decide(check.id, { status: decision, note: note || undefined }),
    onSuccess: (r) => {
      toast.push(
        `Decision recorded: ${r.status} for ${r.user_email}`,
        decision === "PASS" ? "success" : "warn",
      );
      qc.invalidateQueries({ queryKey: ["admin-sanctions"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Decide · ${check.user_email ?? check.user_id}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          decide.mutate();
        }}
        className="space-y-3"
      >
        <p className="text-2xs text-term-dim">
          Vendor <span className="font-mono">{check.vendor}</span> returned{" "}
          {check.hit_count} match{check.hit_count === 1 ? "" : "es"} in
          categories: <span className="font-mono">{check.hit_categories.join(", ") || "—"}</span>
          . Adjudicate this row to PASS to clear the user for trading, or
          FAIL to keep them blocked.
        </p>
        <div>
          <Label>Decision</Label>
          <Select
            value={decision}
            onChange={(e) => setDecision(e.target.value as "PASS" | "FAIL")}
          >
            <option value="PASS">PASS — clear for trading</option>
            <option value="FAIL">FAIL — keep blocked</option>
          </Select>
        </div>
        <div>
          <Label>Note (visible in audit log)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. low-confidence PEP match — same name, different DOB"
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={decide.isPending}>
            Save decision
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RescanButton({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const rescan = useMutation({
    mutationFn: () => sanctions.rescan(userId),
    onSuccess: (r) => {
      toast.push(`Rescan: ${r.status} (${r.hit_count} hits)`, "info");
      qc.invalidateQueries({ queryKey: ["admin-sanctions"] });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
      toast.push(`Rescan failed: ${msg}`, "error");
    },
  });
  return (
    <Button variant="ghost" onClick={() => rescan.mutate()} loading={rescan.isPending}>
      Rescan
    </Button>
  );
}
